import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaChartBar, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { MdDashboard, MdManageAccounts } from "react-icons/md";
import { FaRegUser } from "react-icons/fa";
import { GiExpense } from "react-icons/gi";
import { MdOutlineSignalCellularAlt1Bar } from "react-icons/md";
import "./Sidebar.css";

const Sidebar = ({
  collapsed,
  toggleSidebar,
  isOpen,
  closeSidebar,
  isMobile,
}) => {
  const location = useLocation();
  const [openDropdowns, setOpenDropdowns] = useState({
    bill: false,
    masters: false,
    users: false,
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);

  // Get role from localStorage
  const role = localStorage.getItem("role") || "Receptionist";

  const menuItemRefs = useRef({});

  useEffect(() => {
    const currentPath = location.pathname;
    const dropdownPaths = ["/bill", "/masters", "/users"];
    const isDropdownPath = dropdownPaths.some((path) =>
      currentPath.startsWith(path),
    );
    if (!isDropdownPath && !openDropdowns.logoClicked) {
      setOpenDropdowns({
        bill: false,
        masters: false,
        users: false,
        logoClicked: false,
      });
    }
  }, [location.pathname]);

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownName]: !prev[dropdownName],
      logoClicked: false,
    }));
  };

  const handleSubItemClick = (parentDropdown) => {
    setOpenDropdowns({
      bill: parentDropdown === "bill",
      masters: parentDropdown === "masters",
      users: parentDropdown === "users",
      logoClicked: false,
    });
  };

  const handleMouseEnter = (itemIndex, event) => {
    if (!isMobile && collapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredItem({
        index: itemIndex,
        top: rect.top,
        left: rect.right + 10,
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleTooltipMouseEnter = (itemIndex, itemName, event) => {
    if (!isMobile && collapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredTooltip({
        index: itemIndex,
        name: itemName,
        top: rect.top + rect.height / 2 - 20,
        left: rect.right + 10,
      });
    }
  };

  const handleTooltipMouseLeave = () => {
    setHoveredTooltip(null);
  };

  // Define menu items based on role
  const fullMenuItems = [
    { name: "Dashboard", icon: <MdDashboard />, path: "/dashboard" },
    {
      name: "Bill",
      icon: <GiExpense />,
      path: "/bill",
      dropdown: "bill",
      subItems: [
        { name: "General", path: "/bill/general" },
        { name: "Motorola", path: "/bill/motorola" },
        { name: "GST-IN", path: "/bill/gstbill" },
      ],
    },
    {
      name: "Masters",
      icon: <MdManageAccounts />,
      path: "/masters",
      dropdown: "masters",
      subItems: [
        { name: "Brands", path: "/masters/brand" },
        { name: "Models", path: "/masters/models" },
        { name: "Charges", path: "/masters/charges" },
        { name: "Staffs", path: "/masters/staffs" },
      ],
    },
    {
      name: "Users",
      icon: <FaRegUser />,
      path: "/users",
    },
    { name: "Reports", icon: <FaChartBar />, path: "/reports" },
  ];

  const receptionistMenuItems = [
    { name: "Dashboard", icon: <MdDashboard />, path: "/dashboard" },
    {
      name: "Bill",
      icon: <GiExpense />,
      path: "/bill",
      dropdown: "bill",
      subItems: [
        { name: "General", path: "/bill/general" },
        { name: "Motorola", path: "/bill/motorola" },
      ],
    },
    { name: "Reports", icon: <FaChartBar />, path: "/reports" },
  ];

  const menuItems =
    role === "Receptionist" ? receptionistMenuItems : fullMenuItems;

  const itemHasSubItems = (path) => {
    const found = menuItems.find((item) => item.path === path);
    return found?.subItems?.length > 0;
  };

  const handleItemClick = (itemPath) => {
    setOpenDropdowns({
      bill: false,
      masters: false,
      users: false,
      logoClicked: false,
    });
    if (isMobile && !itemHasSubItems(itemPath)) {
      closeSidebar();
    } else if (!isMobile && collapsed) {
      toggleSidebar();
    }
  };

  const handleDropdownClick = (dropdownName) => {
    if (!collapsed) {
      toggleDropdown(dropdownName);
    }
  };

  const handleSubmenuItemClick = (subItemPath, parentDropdown) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [parentDropdown]: false,
      logoClicked: false,
    }));
    if (isMobile) {
      closeSidebar();
    }
    if (
      !isMobile &&
      (subItemPath === "/bill/general" ||
        subItemPath === "/bill/gstbill" ||
        subItemPath === "/bill/motorola")
    ) {
      toggleSidebar();
    }
  };

  const handleLogoClick = () => {
    if (!isMobile) {
      toggleSidebar();
      setOpenDropdowns({
        bill: false,
        masters: false,
        users: false,
        logoClicked: true,
      });
    }
  };

  return (
    <>
      <div
        className={`sidebar-container ${
          !isMobile && collapsed ? "collapsed" : ""
        } ${isOpen ? "open" : ""}`}
      >
        <div className="sidebar-header">
          <div className="logo-container" onClick={handleLogoClick}>
            {!isMobile && collapsed ? (
              <img
                alt="Logo"
                className="logo-collapsed"
                src={require("../../assets/images/Icon.png")}
              />
            ) : (
              <div className="logo-with-text">
                <img
                  alt="Logo"
                  className="logo-full"
                  src={require("../../assets/images/logo.png")}
                  style={{ objectFit: "cover" }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sidebar-section">
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => (
              <React.Fragment key={index}>
                <li
                  ref={(el) => (menuItemRefs.current[index] = el)}
                  className={`sidebar-menu-item ${
                    location.pathname === item.path ||
                    (item.subItems &&
                      item.subItems.some((subItem) =>
                        location.pathname.startsWith(subItem.path),
                      ))
                      ? "active"
                      : ""
                  } ${
                    item.subItems && !isMobile && collapsed ? "has-submenu" : ""
                  }`}
                  onMouseEnter={(e) => {
                    if (item.subItems) {
                      handleMouseEnter(index, e);
                    } else {
                      handleTooltipMouseEnter(index, item.name, e);
                    }
                  }}
                  onMouseLeave={() => {
                    if (item.subItems) {
                      handleMouseLeave();
                    } else {
                      handleTooltipMouseLeave();
                    }
                  }}
                >
                  {item.subItems ? (
                    <div
                      className="menu-link"
                      onClick={() => handleDropdownClick(item.dropdown)}
                    >
                      <span className="menu-icon">{item.icon}</span>
                      {(!isMobile || !collapsed) && (
                        <>
                          <span className="menu-text">{item.name}</span>
                          <span className="dropdown-arrow">
                            {openDropdowns[item.dropdown] ? (
                              <FaChevronDown />
                            ) : (
                              <FaChevronRight />
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className="menu-link"
                      onClick={() => handleItemClick(item.path)}
                    >
                      <span className="menu-icon">{item.icon}</span>
                      {(!isMobile || !collapsed) && (
                        <span className="menu-text">{item.name}</span>
                      )}
                    </Link>
                  )}
                </li>

                {/* Hover Submenu */}
                {item.subItems &&
                  !isMobile &&
                  collapsed &&
                  hoveredItem &&
                  hoveredItem.index === index && (
                    <div
                      className="hover-submenu"
                      style={{
                        position: "fixed",
                        top: hoveredItem.top,
                        left: "70px",
                        zIndex: 1002,
                      }}
                      onMouseEnter={() => setHoveredItem(hoveredItem)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="hover-submenu-header">
                        <span className="menu-icon">{item.icon}</span>
                        <span className="menu-text">{item.name}</span>
                      </div>
                      <ul className="hover-submenu-list">
                        {item.subItems.map((subItem, subIndex) => (
                          <li key={subIndex} className="hover-submenu-item">
                            <Link
                              to={subItem.path}
                              className="hover-submenu-link"
                              onClick={() =>
                                handleSubmenuItemClick(
                                  subItem.path,
                                  item.dropdown,
                                )
                              }
                            >
                              <div className="d-flex align-items-center gap-2">
                                <div className="d-flex flex-column align-items-center">
                                  <MdOutlineSignalCellularAlt1Bar size={12} />
                                  <MdOutlineSignalCellularAlt1Bar size={12} />
                                </div>
                                <span>{subItem.name}</span>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Tooltip for single items */}
                {!item.subItems &&
                  !isMobile &&
                  collapsed &&
                  hoveredTooltip &&
                  hoveredTooltip.index === index && (
                    <div
                      className="menu-tooltip"
                      style={{
                        position: "fixed",
                        top: hoveredTooltip.top,
                        left: "70px",
                        zIndex: 1002,
                      }}
                    >
                      <div className="tooltip-content">
                        <span className="tooltip-text">
                          {hoveredTooltip.name}
                        </span>
                      </div>
                    </div>
                  )}

                {/* Normal Submenu */}
                {item.subItems &&
                  openDropdowns[item.dropdown] &&
                  !collapsed && (
                    <ul className="submenu mt-2">
                      {item.subItems.map((subItem, subIndex) => (
                        <li
                          key={subIndex}
                          className={`submenu-item ${
                            location.pathname.startsWith(subItem.path)
                              ? "active"
                              : ""
                          }`}
                        >
                          <Link
                            to={subItem.path}
                            className="submenu-link"
                            onClick={() =>
                              handleSubmenuItemClick(
                                subItem.path,
                                item.dropdown,
                              )
                            }
                          >
                            <div className="d-flex align-items-center gap-2">
                              <div className="d-flex flex-column align-items-center">
                                <MdOutlineSignalCellularAlt1Bar size={18} />
                                <MdOutlineSignalCellularAlt1Bar size={18} />
                              </div>
                              <span className="mt-1">{subItem.name}</span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}
    </>
  );
};

export default Sidebar;
